import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../ui/form";
import { useForm } from "react-hook-form";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { User } from "../../lib/interfaces/user.interface";
import { zodResolver } from "@hookform/resolvers/zod";
import { FC, useEffect, useState } from "react";
import { sabonnerFormSchema } from "../../lib/schemas/sabonner-form-schema";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { z } from "zod";
import { Civilite } from "../../lib/interfaces/civilite.interface";
import { Switch } from "../ui/switch";
import Title from "../ui/Title";
import Box from "../ui/Box";
import { loadStripe } from "@stripe/stripe-js";
import { Tarif } from "../../lib/interfaces/tarif.interface";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import ChoixPack from "./ChoixPack";
import Checkout from "./Checkout";
import { useAtom, useAtomValue } from "jotai";
import { modeDePaiementAtom, packAtom } from "../../lib/atoms";
import { Alert } from "../ui/alert";
import { toast } from "sonner";
import axiosClient from "../../lib/axiosClient";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { Pack } from "../../lib/interfaces/pack.interface";
import { TypePays } from "../../lib/enums/type-pays.enum";
import { useQueryClient } from "@tanstack/react-query";
import { ksUserQK } from "../../lib/query-keys";

interface Props {
  user: User;
  civilites: Civilite[];
  packs: Pack[];
  tarif: Tarif;
  type: "papier" | "numerique";
}

const SabonnerForm: FC<Props> = ({ user, civilites, packs, tarif, type }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pack, setPack] = useAtom(packAtom);
  const modeDePaiement = useAtomValue(modeDePaiementAtom);
  const [parent] = useAutoAnimate();
  const [parent2] = useAutoAnimate();
  const [parent3] = useAutoAnimate();
  const form = useForm<z.infer<typeof sabonnerFormSchema>>({
    resolver: zodResolver(sabonnerFormSchema),
    defaultValues: {
      siret: user.siret || "",
      adresse1: user.adresse1 || "",
      adresse1Livraison: user.adresse1Livraison || "",
      adresse2: user.adresse2 || "",
      adresse2Livraison: user.adresse2Livraison || "",
      civiliteId: user.civiliteId || "",
      codePostal: user.codePostal || "",
      codePostalLivraison: user.codePostalLivraison || "",
      dateDeNaissance: !!user.dateDeNaissance ? user.dateDeNaissance : "",
      envoyerAdresseDifferente: user.envoyerAdresseDifferente || false,
      nom: user.nom || "",
      nomLivraison: user.nomLivraison || "",
      entrepriseLivraison: user.entrepriseLivraison || "",
      typePays: user.typePays === TypePays.F ? TypePays.FRANCE : user.typePays || TypePays.FRANCE,
      pays: user.pays || "FRANCE",
      paysLivraison: user.paysLivraison || "FRANCE",
      personneMorale: user.personneMorale || false,
      prenom: user.prenom || "",
      raisonSociale: user.raisonSociale || "",
      telephone: user.telephone || "",
      typePaysLivraison:
        user.typePaysLivraison === TypePays.F || user.typePaysLivraison === TypePays.FRANCE
          ? TypePays.FRANCE
          : user.typePaysLivraison || TypePays.FRANCE,
      ville: user.ville || "",
      villeLivraison: user.villeLivraison || "",
      nbRuches: 0,
    },
  });

  useEffect(() => {
    if (form.watch("typePays") === TypePays.UE || form.watch("typePays") === TypePays.HORS_UE) {
      setPack(null);
    }
    if (form.watch("typePays") === TypePays.FRANCE) {
      form.setValue("pays", "FRANCE");
    }
  }, [form.watch("typePays")]);

  useEffect(() => {
    if (form.watch("typePaysLivraison") === TypePays.FRANCE) {
      form.setValue("paysLivraison", "FRANCE");
    }
  }, [form.watch("typePaysLivraison")]);

  const handleSubmit = async (data: z.infer<typeof sabonnerFormSchema>) => {
    // Valider un pack choisi
    if (pack === undefined) {
      setError("Veuillez préciser si vous avez besoin d'un pack d'options.");
      return;
    }
    // Vérifier que le nombre de ruches est indiqué avec le pack
    if (!!pack && data.nbRuches === 0) {
      setError(
        "Vous avez choisi un pack d'options avec assurance apicole et l'éco-contribution mais avez laissé votre nombre de ruches à 0. Veuillez préciser le nombre de ruches que vous souhaitez assurer."
      );
      return;
    }

    // Vérifier qu'il y a un mode de paiement
    if (!modeDePaiement) {
      setError("Veuillez renseigner votre mode de paiement.");
      return;
    }
    setError("");

    try {
      setLoading(true);

      // Vérifier que le user est autorisé
      const { data: allowReabonnement } = await axiosClient.get(`/abonnements/reabonnement-disponible`);
      if (!allowReabonnement) {
        toast.error("Vous avez déjà un abonnement en cours.");
        setLoading(false);
        return;
      }

      // Mettre à jour les coordonnées
      const coords = {
        nom: data.nom.trim().toUpperCase(),
        prenom: data.prenom.trim().toUpperCase(),
        siret: data.siret.trim(),
        civiliteId: data.personneMorale ? null : data.civiliteId,
        raisonSociale: data.personneMorale ? data.raisonSociale?.trim()?.toUpperCase() : "",
        dateDeNaissance: data.personneMorale ? "" : data.dateDeNaissance,
        adresse1: data.adresse1.trim().toUpperCase(),
        adresse2: data.adresse2.trim().toUpperCase() || "",
        codePostal: data.codePostal.trim(),
        ville: data.ville.trim().toUpperCase(),
        pays: data.typePays === TypePays.FRANCE ? "FRANCE" : data.pays?.trim()?.toUpperCase(),
        telephone: data.telephone.trim(),
        typePays: data.typePays,
        personneMorale: data.personneMorale,
        // Adresse de livraison
        envoyerAdresseDifferente: type === "papier" ? data.envoyerAdresseDifferente : false,
        nomLivraison: data.envoyerAdresseDifferente ? data.nomLivraison.trim().toUpperCase() : "",
        entrepriseLivraison: data.envoyerAdresseDifferente ? data.entrepriseLivraison.trim().toUpperCase() : "",
        adresse1Livraison: data.envoyerAdresseDifferente ? data.adresse1Livraison?.trim()?.toUpperCase() : "",
        adresse2Livraison: data.envoyerAdresseDifferente ? data.adresse2Livraison?.trim()?.toUpperCase() || "" : "",
        codePostalLivraison: data.envoyerAdresseDifferente ? data.codePostalLivraison?.trim() : "",
        villeLivraison: data.envoyerAdresseDifferente ? data.villeLivraison?.trim()?.toUpperCase() || "" : "",
        paysLivraison: data.envoyerAdresseDifferente
          ? data.typePaysLivraison === TypePays.FRANCE
            ? "FRANCE"
            : data.paysLivraison?.trim()?.toUpperCase()
          : "FRANCE",
        typePaysLivraison: data.envoyerAdresseDifferente ? data.typePaysLivraison : data.typePays,
      };
      await axiosClient.patch(`/users/${user.id}/coordonnees`, coords);

      // Créer la facture
      const { data: factureId } = await axiosClient.post<string>(`/abonnements/v2/direct`, {
        packId: pack?.id,
        ruches: data.nbRuches,
        modeDePaiement,
        revueNumerique: type === "numerique",
      });

      if (modeDePaiement === "CB") {
        const { data: stripeCheckoutSessionId } = await axiosClient.post<string>(
          `/paiement/creer-stripe-checkout-session-pour-facture`,
          { factureId }
        );
        const stripe = await loadStripe(`${import.meta.env.VITE_STRIPE_KEY}`);
        stripe?.redirectToCheckout({ sessionId: stripeCheckoutSessionId });
      } else {
        // Rediriger
        queryClient.invalidateQueries({ queryKey: [ksUserQK] });
        navigate(`/paiement/succes?type=${modeDePaiement === "VIR" ? "vir" : "chq"}`);
      }
    } catch (error: any) {
      toast.error("Une erreur s'est produite. Veuillez réessayer ultérieurement.");
      console.error(error?.response?.data);
      setLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Box className='grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-4'>
          <div className='lg:col-span-12 mb-2'>
            <Title order={3}>Coordonnées de facturation</Title>
          </div>

          {/* Société ? */}
          <div className='lg:col-span-12'>
            <FormField
              control={form.control}
              name='personneMorale'
              render={({ field }) => (
                <FormItem className='flex flex-row items-center gap-4'>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className='space-y-0.5'>
                    <FormLabel className='text-base'>Cet abonnement concerne une société</FormLabel>
                  </div>
                </FormItem>
              )}
            />
          </div>

          {/* Civilité */}
          {!form.watch("personneMorale") && (
            <div className='lg:col-span-2'>
              <FormField
                control={form.control}
                name='civiliteId'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Civilité</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder='Sélectionner' />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {civilites.map((civ) => (
                          <SelectItem key={civ.id} value={civ.id}>
                            {civ.nom}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
          {/* Raison sociale */}
          {form.watch("personneMorale") && (
            <div className='lg:col-span-4'>
              <FormField
                control={form.control}
                name='raisonSociale'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Raison sociale</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
          {/* Nom */}
          <div className='lg:col-span-3'>
            <FormField
              control={form.control}
              name='nom'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* Prénom */}
          <div className='lg:col-span-3'>
            <FormField
              control={form.control}
              name='prenom'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prénom</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* Date de naissance */}
          {!form.watch("personneMorale") && (
            <div className='lg:col-span-2'>
              <FormField
                control={form.control}
                name='dateDeNaissance'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date de naissance</FormLabel>
                    <FormControl>
                      <Input type='date' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
          {/* Telephone */}
          <div className='lg:col-span-2'>
            <FormField
              control={form.control}
              name='telephone'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Téléphone</FormLabel>
                  <FormControl>
                    <Input placeholder='En cas de problème' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* Adresse 1 */}
          <div className='lg:col-span-4'>
            <FormField
              control={form.control}
              name='adresse1'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>N° et rue ou lieu dit</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* Adresse 2 */}
          <div className='lg:col-span-3'>
            <FormField
              control={form.control}
              name='adresse2'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Complément d'adresse</FormLabel>
                  <FormControl>
                    <Input placeholder='Appartement, Bâtiment, Résidence, BP' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* Code postal */}
          <div className='lg:col-span-2'>
            <FormField
              control={form.control}
              name='codePostal'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Code postal</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* Ville */}
          <div className='lg:col-span-3'>
            <FormField
              control={form.control}
              name='ville'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ville</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Type de pays */}
          <div className='lg:col-span-5'>
            <FormField
              control={form.control}
              name='typePays'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pays ou territoire</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder='Sélectionner' />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={TypePays.FRANCE}>France métropolitaine</SelectItem>
                      <SelectItem value={TypePays.DROM_COM}>DROM-COM sauf Guyane et Mayotte</SelectItem>
                      <SelectItem value={TypePays.MAYOTTE_GUYANNE}>Mayotte ou Guyane</SelectItem>
                      <SelectItem value={TypePays.UE}>Union Européenne</SelectItem>
                      <SelectItem value={TypePays.HORS_UE}>Hors Union Européenne</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* Préciser le pays */}
          <div className='lg:col-span-4'>
            <FormField
              control={form.control}
              name='pays'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Préciser le pays ou territoire</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={form.watch("typePays") === "F"} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* Préciser le pays */}
          <div className='lg:col-span-3'>
            <FormField
              control={form.control}
              name='nbRuches'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Votre nombre de ruches</FormLabel>
                  <FormControl>
                    <Input required min={0} {...field} type='number' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {/* SIRET */}
          <div className='lg:col-span-4'>
            <FormField
              control={form.control}
              name='siret'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>N° SIRET</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Box>
        {/* Envoyer à une adresse différente ? */}
        {type === "papier" && (
          <FormField
            control={form.control}
            name='envoyerAdresseDifferente'
            render={({ field }) => (
              <FormItem className='flex flex-row items-center gap-4 mb-4 mt-5'>
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <div className='space-y-0.5'>
                  <FormLabel className='text-base'>Je souhaite recevoir la revue à une adresse différente</FormLabel>
                </div>
              </FormItem>
            )}
          />
        )}

        {/* Formulaire d'adresse de livraison */}
        <div ref={parent}>
          {form.watch("envoyerAdresseDifferente") && (
            <Box className='grid grid-cols-1 lg:grid-cols-12 md:gap-4'>
              <div className='lg:col-span-12'>
                <Title order={3}>Adresse de livraison</Title>
              </div>
              {/* Nom livraison */}
              <div className='lg:col-span-6'>
                <FormField
                  control={form.control}
                  name='nomLivraison'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nom et prénom</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {/* Entreprise livraison */}
              <div className='lg:col-span-6'>
                <FormField
                  control={form.control}
                  name='entrepriseLivraison'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Entreprise</FormLabel>
                      <FormControl>
                        <Input placeholder='Facultatif' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {/* Adresse 1 livraison */}
              <div className='lg:col-span-6'>
                <FormField
                  control={form.control}
                  name='adresse1Livraison'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>N° et rue ou lieu dit</FormLabel>
                      <FormControl>
                        <Input required {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {/* Adresse 2 livraison */}
              <div className='lg:col-span-6'>
                <FormField
                  control={form.control}
                  name='adresse2Livraison'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Appartement, Bâtiment, Résidence, BP</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {/* Code postal livraison */}
              <div className='lg:col-span-2'>
                <FormField
                  control={form.control}
                  name='codePostalLivraison'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Code postal</FormLabel>
                      <FormControl>
                        <Input required {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {/* Ville livraison */}
              <div className='lg:col-span-3'>
                <FormField
                  control={form.control}
                  name='villeLivraison'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ville</FormLabel>
                      <FormControl>
                        <Input required {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Type de pays livraison */}
              <div className='lg:col-span-3'>
                <FormField
                  control={form.control}
                  name='typePaysLivraison'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pays ou territoire</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder='Sélectionner' />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={TypePays.FRANCE}>France métropolitaine</SelectItem>
                          <SelectItem value={TypePays.DROM_COM}>DROM-COM sauf Guyane ou Mayotte</SelectItem>
                          <SelectItem value={TypePays.MAYOTTE_GUYANNE}>Mayotte ou Guyane</SelectItem>
                          <SelectItem value={TypePays.UE}>Union Européenne</SelectItem>
                          <SelectItem value={TypePays.HORS_UE}>Hors Union Européenne</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {/* Préciser le pays de livraison */}
              <div className='lg:col-span-4'>
                <FormField
                  control={form.control}
                  name='paysLivraison'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Préciser le pays ou territoire</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          required={
                            form.watch("envoyerAdresseDifferente") &&
                            form.watch("typePaysLivraison") !== TypePays.FRANCE
                          }
                          disabled={form.watch("typePaysLivraison") === TypePays.FRANCE}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </Box>
          )}
        </div>
        <div ref={parent3}>
          {form.watch("typePays") !== TypePays.UE && form.watch("typePays") !== TypePays.HORS_UE && (
            <div className='mt-8'>
              <ChoixPack packs={packs} />
            </div>
          )}
        </div>

        {/* Panier et mode de paiement */}
        <Checkout
          nbRuches={form.watch("nbRuches")}
          tarif={tarif}
          typeAbonnement={type}
          typePays={form.watch("envoyerAdresseDifferente") ? form.watch("typePaysLivraison") : form.watch("typePays")}
        />

        <div ref={parent2}>
          {!!error && (
            <Alert className='bg-red-100 mt-5' variant={"destructive"}>
              {error}
            </Alert>
          )}
          {Object.keys(form.formState.errors).length > 0 && (
            <Alert className='bg-red-100 mt-5' variant={"destructive"}>
              Des erreurs ont été trouvées dans le formulaire.
            </Alert>
          )}
        </div>

        <div className='text-right mb-8 mt-6 md:mt-5'>
          <div>
            <Button type='submit' loading={loading} icon={<Check className='w-5 h-5' />}>
              Valider et s'engager à payer
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};

export default SabonnerForm;
